import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useOrganization } from '@clerk/clerk-react';
import { useApi } from '../lib/api';

interface VocabularyTerm {
  id: string;
  canonicalTerm: string;
  variant: string;
  confidence: string;
  timesSeen: number;
  timesConfirmed: number;
  learnedFrom: string;
  createdAt: string;
}

interface VocabularySettings {
  autoLearnFromCorrections: boolean;
  requireAdminApproval: boolean;
  minOccurrencesToLearn: number;
  shareVocabularyWithGlobal: boolean;
}

interface VocabularyStats {
  totalTerms: number;
  totalVariants: number;
  avgConfidence: number;
  mostUsedTerms: {
    canonicalTerm: string;
    variantCount: number;
    totalSeen: number;
  }[];
}

export function OrgVocabulary() {
  const { organization } = useOrganization();
  const { get, post, patch, delete: del } = useApi();

  const [vocabulary, setVocabulary] = useState<VocabularyTerm[]>([]);
  const [stats, setStats] = useState<VocabularyStats | null>(null);
  const [settings, setSettings] = useState<VocabularySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingTerm, setEditingTerm] = useState<VocabularyTerm | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    canonicalTerm: '',
    variant: '',
    confidence: '0.5',
  });
  const [csvContent, setCsvContent] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    if (!organization) return;

    try {
      setLoading(true);
      setError(null);

      const [vocabRes, statsRes, settingsRes] = await Promise.all([
        get<{ vocabulary: VocabularyTerm[]; total: number }>(
          `/organizations/${organization.id}/vocabulary${searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : ''}`
        ),
        get<VocabularyStats>(`/organizations/${organization.id}/vocabulary/stats`),
        get<VocabularySettings>(`/organizations/${organization.id}/vocabulary/settings`),
      ]);

      setVocabulary(vocabRes.vocabulary);
      setStats(statsRes);
      setSettings(settingsRes);
    } catch (err) {
      console.error('Error fetching vocabulary:', err);
      setError(err instanceof Error ? err.message : 'Failed to load vocabulary');
    } finally {
      setLoading(false);
    }
  }, [organization, searchQuery, get]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  async function handleAddTerm(e: React.FormEvent) {
    e.preventDefault();
    if (!organization) return;

    try {
      setSaving(true);
      setFormError(null);

      if (editingTerm) {
        await patch(`/organizations/${organization.id}/vocabulary/${editingTerm.id}`, formData);
      } else {
        await post(`/organizations/${organization.id}/vocabulary`, formData);
      }

      setShowAddModal(false);
      setEditingTerm(null);
      setFormData({ canonicalTerm: '', variant: '', confidence: '0.5' });
      fetchData();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!organization) return;
    if (!confirm('Delete this vocabulary mapping?')) return;

    try {
      await del(`/organizations/${organization.id}/vocabulary/${id}`);
      fetchData();
    } catch (err) {
      alert('Failed to delete');
    }
  }

  async function handleImport(e: React.FormEvent) {
    e.preventDefault();
    if (!organization) return;

    try {
      setSaving(true);
      setFormError(null);

      const result = await post<{ imported: number; skipped: number; errors: string[] }>(
        `/organizations/${organization.id}/vocabulary/import`,
        { csv: csvContent }
      );

      alert(`Imported ${result.imported} terms. Skipped ${result.skipped}.${result.errors.length > 0 ? `\n\nErrors:\n${result.errors.slice(0, 5).join('\n')}` : ''}`);

      setShowImportModal(false);
      setCsvContent('');
      fetchData();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdateSettings(newSettings: Partial<VocabularySettings>) {
    if (!organization) return;

    try {
      setSaving(true);
      const updated = await patch<VocabularySettings>(
        `/organizations/${organization.id}/vocabulary/settings`,
        newSettings
      );
      setSettings(updated);
    } catch (err) {
      alert('Failed to update settings');
    } finally {
      setSaving(false);
    }
  }

  function openEditModal(term: VocabularyTerm) {
    setEditingTerm(term);
    setFormData({
      canonicalTerm: term.canonicalTerm,
      variant: term.variant,
      confidence: term.confidence,
    });
    setShowAddModal(true);
  }

  if (!organization) {
    return (
      <div className="bg-slate-800 rounded-lg p-8 text-center border border-slate-700">
        <p className="text-slate-400">Organization context required</p>
        <p className="mt-2 text-sm text-slate-500">
          Select an organization to manage vocabulary
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <nav className="text-sm text-slate-400 mb-2">
            <Link to="/org/dashboard" className="hover:text-green-400">Organization</Link>
            <span className="mx-2">/</span>
            <span className="text-white">Vocabulary</span>
          </nav>
          <h1 className="text-2xl font-bold text-white">Custom Vocabulary</h1>
          <p className="mt-1 text-slate-400">
            Teach DemoPath your organization's terminology for better matching
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowSettingsModal(true)}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded-md transition-colors"
          >
            Settings
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded-md transition-colors"
          >
            Import CSV
          </button>
          <button
            onClick={() => {
              setEditingTerm(null);
              setFormData({ canonicalTerm: '', variant: '', confidence: '0.5' });
              setShowAddModal(true);
            }}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md transition-colors"
          >
            Add Term
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard label="Total Terms" value={stats.totalTerms.toString()} />
          <StatCard label="Total Variants" value={stats.totalVariants.toString()} />
          <StatCard
            label="Avg Confidence"
            value={`${(stats.avgConfidence * 100).toFixed(0)}%`}
            valueColor={stats.avgConfidence >= 0.8 ? 'text-green-500' : stats.avgConfidence >= 0.5 ? 'text-yellow-500' : 'text-slate-400'}
          />
          <StatCard
            label="Most Used"
            value={stats.mostUsedTerms[0]?.canonicalTerm || '-'}
            subtext={stats.mostUsedTerms[0] ? `${stats.mostUsedTerms[0].totalSeen} matches` : ''}
          />
        </div>
      )}

      {/* Search */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Search terms or variants..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-md text-white placeholder-slate-500 focus:outline-none focus:border-green-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
            >
              &times;
            </button>
          )}
        </div>
      </div>

      {/* Vocabulary Table */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-900/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                Canonical Term
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                Variant (Spoken)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                Confidence
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                Usage
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                Source
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {vocabulary.length > 0 ? (
              vocabulary.map((term) => (
                <tr key={term.id} className="hover:bg-slate-700/30 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-white font-medium">
                    {term.canonicalTerm}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-slate-300">
                    {term.variant}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`${
                        parseFloat(term.confidence) >= 0.8
                          ? 'text-green-500'
                          : parseFloat(term.confidence) >= 0.5
                          ? 'text-yellow-500'
                          : 'text-slate-400'
                      }`}
                    >
                      {(parseFloat(term.confidence) * 100).toFixed(0)}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-slate-400 text-sm">
                    {term.timesSeen} seen / {term.timesConfirmed} confirmed
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        term.learnedFrom === 'admin_added'
                          ? 'bg-blue-500/20 text-blue-400'
                          : term.learnedFrom === 'manual_override'
                          ? 'bg-purple-500/20 text-purple-400'
                          : 'bg-slate-500/20 text-slate-400'
                      }`}
                    >
                      {term.learnedFrom === 'admin_added'
                        ? 'Admin'
                        : term.learnedFrom === 'manual_override'
                        ? 'Learned'
                        : term.learnedFrom}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => openEditModal(term)}
                      className="text-slate-400 hover:text-white mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(term.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-slate-400">
                  {searchQuery ? (
                    <>No vocabulary found matching "{searchQuery}"</>
                  ) : (
                    <>
                      <p>No vocabulary terms yet</p>
                      <p className="mt-2 text-sm text-slate-500">
                        Add terms manually or import from CSV
                      </p>
                    </>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <Modal title={editingTerm ? 'Edit Term' : 'Add Vocabulary Term'} onClose={() => setShowAddModal(false)}>
          <form onSubmit={handleAddTerm} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Canonical Term (correct spelling)
              </label>
              <input
                type="text"
                value={formData.canonicalTerm}
                onChange={(e) => setFormData({ ...formData, canonicalTerm: e.target.value })}
                placeholder="e.g., Kubernetes"
                className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-md text-white focus:outline-none focus:border-green-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Variant (how it's spoken/recognized)
              </label>
              <input
                type="text"
                value={formData.variant}
                onChange={(e) => setFormData({ ...formData, variant: e.target.value })}
                placeholder="e.g., K8s, kube, kubernetes"
                className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-md text-white focus:outline-none focus:border-green-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Confidence ({(parseFloat(formData.confidence) * 100).toFixed(0)}%)
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={formData.confidence}
                onChange={(e) => setFormData({ ...formData, confidence: e.target.value })}
                className="w-full"
              />
              <p className="text-xs text-slate-500 mt-1">
                Higher confidence = more likely to match
              </p>
            </div>
            {formError && (
              <div className="text-red-400 text-sm">{formError}</div>
            )}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingTerm ? 'Update' : 'Add Term'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <Modal title="Import Vocabulary from CSV" onClose={() => setShowImportModal(false)}>
          <form onSubmit={handleImport} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                CSV Content
              </label>
              <textarea
                value={csvContent}
                onChange={(e) => setCsvContent(e.target.value)}
                placeholder="canonical,variant,confidence&#10;Kubernetes,K8s,0.9&#10;Kubernetes,kube,0.8"
                rows={8}
                className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-md text-white font-mono text-sm focus:outline-none focus:border-green-500"
                required
              />
              <p className="text-xs text-slate-500 mt-1">
                Format: canonical,variant[,confidence]. Confidence is optional (defaults to 0.5).
              </p>
            </div>
            {formError && (
              <div className="text-red-400 text-sm">{formError}</div>
            )}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md disabled:opacity-50"
              >
                {saving ? 'Importing...' : 'Import'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Settings Modal */}
      {showSettingsModal && settings && (
        <Modal title="Vocabulary Settings" onClose={() => setShowSettingsModal(false)}>
          <div className="space-y-6">
            <SettingToggle
              label="Auto-learn from corrections"
              description="Automatically add new vocabulary when users correct matches"
              checked={settings.autoLearnFromCorrections}
              onChange={(v) => handleUpdateSettings({ autoLearnFromCorrections: v })}
              disabled={saving}
            />
            <SettingToggle
              label="Require admin approval"
              description="Auto-learned terms require admin approval before being used"
              checked={settings.requireAdminApproval}
              onChange={(v) => handleUpdateSettings({ requireAdminApproval: v })}
              disabled={saving}
            />
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Minimum occurrences to learn
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={settings.minOccurrencesToLearn}
                onChange={(e) => handleUpdateSettings({ minOccurrencesToLearn: parseInt(e.target.value) || 2 })}
                className="w-24 px-3 py-2 bg-slate-900 border border-slate-600 rounded-md text-white focus:outline-none focus:border-green-500"
              />
              <p className="text-xs text-slate-500 mt-1">
                How many times a correction must occur before auto-learning
              </p>
            </div>
            <SettingToggle
              label="Share vocabulary with global"
              description="Anonymously contribute your vocabulary to improve matching for everyone"
              checked={settings.shareVocabularyWithGlobal}
              onChange={(v) => handleUpdateSettings({ shareVocabularyWithGlobal: v })}
              disabled={saving}
            />
            <div className="flex justify-end pt-4">
              <button
                onClick={() => setShowSettingsModal(false)}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md"
              >
                Done
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  subtext,
  valueColor = 'text-white',
}: {
  label: string;
  value: string;
  subtext?: string;
  valueColor?: string;
}) {
  return (
    <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
      <p className="text-sm text-slate-400">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${valueColor}`}>{value}</p>
      {subtext && <p className="text-xs text-slate-500 mt-1">{subtext}</p>}
    </div>
  );
}

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-lg border border-slate-700 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-lg font-medium text-white">{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-xl">
            &times;
          </button>
        </div>
        <div className="px-6 py-4">{children}</div>
      </div>
    </div>
  );
}

function SettingToggle({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start justify-between">
      <div>
        <p className="text-white font-medium">{label}</p>
        <p className="text-sm text-slate-400">{description}</p>
      </div>
      <button
        onClick={() => onChange(!checked)}
        disabled={disabled}
        className={`relative w-12 h-6 rounded-full transition-colors ${
          checked ? 'bg-green-600' : 'bg-slate-600'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <span
          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
            checked ? 'translate-x-6' : ''
          }`}
        />
      </button>
    </div>
  );
}
